import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { varzaApi } from '../shared/api/client';
import type { FiscalPeriodStatus, WorkspaceFiscalPeriod } from '../shared/api/types';
import { Dropdown, type DropdownOption } from '../shared/components/Dropdown';
import { Icon } from '../shared/components/Icon';
import { getActiveLocale } from '../shared/i18n/i18n';
import { currencyLabel, formatDate } from '../shared/lib/formatters';
import { useTenantPreference } from '../shared/lib/preferences';
import { useBodyScrollLock, useEscapeKey } from '../shared/lib/responsive';

const periodStatusLabelKey: Readonly<Record<FiscalPeriodStatus, string>> = {
  Open: 'context.openPeriod',
  SoftClosed: 'context.softClosedPeriod',
  HardClosed: 'context.hardClosedPeriod',
};

const periodStatusModifier: Readonly<Record<FiscalPeriodStatus, string>> = {
  Open: 'open',
  SoftClosed: 'soft-closed',
  HardClosed: 'hard-closed',
};

function orderPeriods(periods: readonly WorkspaceFiscalPeriod[]): readonly WorkspaceFiscalPeriod[] {
  return [...periods].sort((left, right) => left.startDate.localeCompare(right.startDate));
}

/**
 * The scope every figure on screen is reported under: organization, branch, fiscal period
 * and functional currency.
 *
 * A phone has no room for four controls beside the search field, and scope is something an
 * operator sets once and then reads. So `compact` renders it as a single summary button that
 * opens the same controls as a sheet — the scope stays visible at a glance, and changing it
 * gets full-width targets instead of a cramped strip.
 */
export function ContextBar({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const locale = getActiveLocale();
  const navigate = useNavigate();
  const { selectTenant, session, tenants } = useAuth();
  const tenantId = session?.selectedTenantId ?? null;
  const [switching, setSwitching] = useState(false);
  const [switchFailed, setSwitchFailed] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [storedBranchId, setStoredBranchId] = useTenantPreference(tenantId, 'branch');
  const [storedPeriodId, setStoredPeriodId] = useTenantPreference(tenantId, 'fiscal-period');

  const closeSheet = useCallback(() => setSheetOpen(false), []);
  useBodyScrollLock(sheetOpen);
  useEscapeKey(sheetOpen, closeSheet);

  const scope = useQuery({
    queryKey: ['workspace-scope', tenantId],
    queryFn: () => varzaApi.workspaceScope(),
    enabled: tenantId !== null,
    staleTime: 5 * 60_000,
  });

  const branches = scope.data?.data.branches ?? [];
  const periods = useMemo(() => orderPeriods(scope.data?.data.fiscalPeriods ?? []), [scope.data]);
  const currencies = scope.data?.data.currencies ?? [];

  const branchId = branches.some((branch) => branch.id === storedBranchId) ? storedBranchId : '';
  const selectedPeriod = periods.find((period) => period.id === storedPeriodId)
    ?? periods.find((period) => period.status === 'Open')
    ?? periods.at(-1)
    ?? null;
  const functionalCurrency = currencies.find((currency) => currency.isFunctional) ?? currencies[0] ?? null;

  const tenantOptions = useMemo<DropdownOption[]>(
    () => tenants.map((tenant) => ({ value: tenant.tenantId, label: tenant.displayName, description: tenant.slug })),
    [tenants],
  );

  const branchOptions = useMemo<DropdownOption[]>(
    () => [
      { value: '', label: t('context.allBranches') },
      ...branches.map((branch) => ({ value: branch.id, label: branch.name, description: branch.code })),
    ],
    [branches, t],
  );

  const periodOptions = useMemo<DropdownOption[]>(
    () => periods.map((period) => ({
      value: period.id,
      label: period.name,
      description: `${formatDate(period.startDate, locale)} – ${formatDate(period.endDate, locale)} · ${t(periodStatusLabelKey[period.status])}`,
      disabled: period.status === 'HardClosed',
    })),
    [locale, periods, t],
  );

  const switchTenant = async (nextTenantId: string) => {
    if (nextTenantId === tenantId) return;
    setSwitching(true);
    setSwitchFailed(false);
    try {
      await selectTenant(nextTenantId);
      navigate('/console');
    } catch {
      setSwitchFailed(true);
    } finally {
      setSwitching(false);
    }
  };

  const tenantName = tenants.find((tenant) => tenant.tenantId === tenantId)?.displayName ?? t('context.tenant');
  const branchName = branches.find((branch) => branch.id === branchId)?.name ?? t('context.allBranches');

  const currencyPanel = (
    <div className="popover__panel popover__panel--end">
      <strong>{t('context.enabledCurrencies')}</strong>
      <ul className="currency-list">
        {currencies.map((currency) => (
          <li key={currency.code} className={`currency-list__item${currency.isEnabled ? '' : ' currency-list__item--disabled'}`}>
            <bdi className="identifier">{currencyLabel(currency.code)}</bdi>
            <span>{currency.name}</span>
            {currency.isFunctional && <em className="currency-tag currency-tag--functional">{t('context.functionalCurrency')}</em>}
            {!currency.isEnabled && <em className="currency-tag">{t('context.disabledCurrency')}</em>}
          </li>
        ))}
      </ul>
      <Link className="text-link" to="/console/currency/rates" onClick={closeSheet}>
        {t('context.viewExchangeRates')}
        <Icon name="arrow" className="icon icon--small directional-icon" />
      </Link>
    </div>
  );

  const periodBadge = selectedPeriod && (
    <span className={`period-badge period-badge--${periodStatusModifier[selectedPeriod.status]}`}>
      <span aria-hidden="true" />
      {t(periodStatusLabelKey[selectedPeriod.status])}
    </span>
  );

  if (compact) {
    return (
      <>
        <button
          className="scope-trigger"
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-expanded={sheetOpen}
          aria-label={t('context.editScope')}
        >
          <span className="scope-trigger__copy">
            <strong>{tenantName}</strong>
            <small>
              {branchName}
              {selectedPeriod && <> · {selectedPeriod.name}</>}
            </small>
          </span>
          <Icon name="chevron" className="icon icon--small directional-icon--down" />
        </button>

        {/*
          The sheet is portalled to the document. Its trigger lives in the top bar, and
          the bar carries a `backdrop-filter` — which makes it the containing block for
          any fixed descendant, so a sheet rendered in place would be pinned to the
          header's 56px box instead of the viewport.
        */}
        {sheetOpen && createPortal(
          <div className="sheet-backdrop" role="presentation" onMouseDown={closeSheet}>
            <section
              className="sheet"
              role="dialog"
              aria-modal="true"
              aria-label={t('context.scope')}
              aria-busy={scope.isLoading || switching}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <header className="sheet__header">
                <span className="sheet__grabber" aria-hidden="true" />
                <h2>{t('context.scope')}</h2>
                <button className="icon-button" type="button" onClick={closeSheet} aria-label={t('common.close')}>
                  <Icon name="close" />
                </button>
              </header>

              <div className="sheet__body">
                {switchFailed && <p className="auth-error" role="alert">{t('auth.tenantError')}</p>}
                {scope.isError && <p className="auth-error" role="alert">{t('context.scopeUnavailable')}</p>}

                <label className="field">
                  <span>{t('context.tenant')}</span>
                  <Dropdown
                    aria-label={t('context.tenant')}
                    value={tenantId ?? ''}
                    onChange={(value) => void switchTenant(value)}
                    options={tenantOptions}
                    disabled={switching || tenants.length < 2}
                    searchable={tenants.length > 6}
                    placeholder={t('context.switchingTenant')}
                  />
                </label>

                <label className="field">
                  <span>{t('context.branch')}</span>
                  <Dropdown
                    aria-label={t('context.branch')}
                    value={branchId}
                    onChange={setStoredBranchId}
                    options={branchOptions}
                    disabled={scope.isLoading}
                    searchable={branches.length > 6}
                  />
                </label>

                <label className="field">
                  <span>{t('context.period')}</span>
                  <Dropdown
                    aria-label={t('context.period')}
                    value={selectedPeriod?.id ?? ''}
                    onChange={setStoredPeriodId}
                    options={periodOptions}
                    disabled={scope.isLoading}
                    searchable={periods.length > 6}
                  />
                </label>

                <div className="sheet__status">
                  {periodBadge}
                  <span className="context-chip">
                    <Icon name="cash" />
                    <span>
                      <small>{t('context.functionalCurrency')}</small>
                      <bdi dir="ltr">{functionalCurrency?.code ?? '—'}</bdi>
                    </span>
                  </span>
                </div>

                <details className="popover currency-popover currency-popover--stacked">
                  <summary className="context-chip context-chip--button">
                    <Icon name="exchange" />
                    <span>{t('context.enabledCurrencies')}</span>
                    <Icon name="chevron" className="icon icon--small directional-icon--down" />
                  </summary>
                  {currencyPanel}
                </details>
              </div>

              <footer className="sheet__footer">
                <button className="button button--primary button--full" type="button" onClick={closeSheet}>
                  {t('common.close')}
                </button>
              </footer>
            </section>
          </div>,
          document.body,
        )}
      </>
    );
  }

  return (
    <div className="context-bar" aria-label={t('app.workspace')} aria-busy={scope.isLoading || switching}>
      <div className="context-bar__scope">
        <div className="context-select">
          <span className="context-select__label">{t('context.tenant')}</span>
          <Dropdown
            aria-label={t('context.tenant')}
            aria-describedby={switchFailed ? 'tenant-switch-error' : undefined}
            value={tenantId ?? ''}
            onChange={(value) => void switchTenant(value)}
            options={tenantOptions}
            disabled={switching || tenants.length < 2}
            searchable={tenants.length > 6}
            placeholder={t('context.switchingTenant')}
            className="context-select__control"
          />
        </div>

        <span className="context-divider" />

        <div className="context-select">
          <span className="context-select__label">{t('context.branch')}</span>
          <Dropdown
            aria-label={t('context.branch')}
            value={branchId}
            onChange={setStoredBranchId}
            options={branchOptions}
            disabled={scope.isLoading}
            searchable={branches.length > 6}
            className="context-select__control"
          />
        </div>

        <span className="context-divider" />

        <div className="context-select">
          <span className="context-select__label">{t('context.period')}</span>
          <Dropdown
            aria-label={t('context.period')}
            value={selectedPeriod?.id ?? ''}
            onChange={setStoredPeriodId}
            options={periodOptions}
            disabled={scope.isLoading}
            searchable={periods.length > 6}
            className="context-select__control context-select__control--wide"
          />
        </div>
      </div>

      <div className="context-bar__status">
        {switchFailed && <span id="tenant-switch-error" className="context-error" role="alert">{t('auth.tenantError')}</span>}
        {scope.isError && <span className="context-error" role="alert">{t('context.scopeUnavailable')}</span>}

        {periodBadge}

        <details className="popover currency-popover">
          <summary className="context-chip context-chip--button" aria-label={t('context.currencyMenu')}>
            <Icon name="cash" />
            <span>
              <small>{t('context.functionalCurrency')}</small>
              <bdi dir="ltr">{functionalCurrency?.code ?? '—'}</bdi>
            </span>
            <Icon name="chevron" className="icon icon--small directional-icon--down" />
          </summary>
          {currencyPanel}
        </details>
      </div>
    </div>
  );
}
